import { motion } from 'framer-motion';
import { ChevronDown, Loader } from 'lucide-react';
import { type ReactNode, useEffect, useRef, useState } from 'react';

import Accordion from '@/components/Accordion/Accordion';
import { cn } from '@/lib/cn';

import { ReasoningTextStyle } from './reasoning-text-style';

type Props = {
  children: ReactNode;
  className?: string;
  isLoading?: boolean;
  initialOpen?: boolean;
};

export const CollapsibleReason = ({
  children,
  className,
  isLoading,
  initialOpen,
}: Props) => {
  if (isLoading)
    return (
      <PreviewDisplay maxHeight={200} className={className}>
        {children}
      </PreviewDisplay>
    );

  return (
    <div className={cn('gap-1.5', className)}>
      <Accordion
        initialOpen={initialOpen}
        anchor={(isOpened) => (
          <button className="text-sm text-gray-600 hover:text-gray-700 px-1 flex items-center cursor-pointer w-fit transition-all duration-200 ease-out ">
            Reasoning
            <motion.div
              animate={{
                rotate: isOpened ? 180 : 0,
              }}
              transition={{
                duration: 0.2,
                ease: 'easeOut',
              }}
              className="ml-1.5"
            >
              <ChevronDown size={12} />
            </motion.div>
          </button>
        )}
        content={<ReasoningTextStyle>{children}</ReasoningTextStyle>}
      />
    </div>
  );
};

const PreviewDisplay = (props: {
  children: ReactNode;
  maxHeight: number | string;
  className?: string;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const hasUserInterruptedAutoScrollRef = useRef(false);
  const isProgrammaticScrollRef = useRef(false);
  const [shrinked, setShrinked] = useState(true);
  const { children, maxHeight } = props;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const interruptAutoScroll = () => {
      hasUserInterruptedAutoScrollRef.current = true;
      isProgrammaticScrollRef.current = false;
    };

    const handleScroll = () => {
      if (isProgrammaticScrollRef.current) {
        return;
      }

      const threshold = 12;
      const isAtBottom =
        container.scrollTop + container.clientHeight >=
        container.scrollHeight - threshold;

      if (!isAtBottom) {
        interruptAutoScroll();
      }
    };
    const handleWheel = (event: WheelEvent) => {
      if (event.deltaY < 0) {
        interruptAutoScroll();
      }
    };
    const handleTouchMove = () => {
      interruptAutoScroll();
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    container.addEventListener('wheel', handleWheel, { passive: true });
    container.addEventListener('touchmove', handleTouchMove, {
      passive: true,
    });

    return () => {
      container.removeEventListener('scroll', handleScroll);
      container.removeEventListener('wheel', handleWheel);
      container.removeEventListener('touchmove', handleTouchMove);
    };
  }, []);

  useEffect(() => {
    if (!containerRef.current) return;
    if (!contentRef.current) return;
    if (hasUserInterruptedAutoScrollRef.current) return;

    const height = contentRef.current.getBoundingClientRect().height;
    isProgrammaticScrollRef.current = true;
    containerRef.current.scrollTo({
      top: height,
      behavior: 'smooth',
    });
    requestAnimationFrame(() => {
      isProgrammaticScrollRef.current = false;
    });
  }, [shrinked, children]);

  return (
    <div className={cn('gap-1.5 relative', props.className)}>
      <button
        className="text-sm text-gray-600 hover:text-gray-700 px-1 flex items-center cursor-pointer w-fit transition-all duration-200 ease-out"
        onClick={() => setShrinked(!shrinked)}
      >
        <Loader className="size-3.5 animate-spin mr-2" />
        Reasoning
        <motion.div
          animate={{
            rotate: !shrinked ? 180 : 0,
          }}
          transition={{
            duration: 0.2,
            ease: 'easeOut',
          }}
          className="ml-1.5"
        >
          <ChevronDown size={12} />
        </motion.div>
      </button>
      <motion.div
        ref={containerRef}
        className="overflow-scroll relative"
        initial={{ height: maxHeight }}
        animate={{ height: shrinked ? maxHeight : 'auto' }}
        transition={{
          duration: 0.2,
          ease: 'easeOut',
        }}
      >
        <ReasoningTextStyle ref={contentRef}>{children}</ReasoningTextStyle>
      </motion.div>
    </div>
  );
};
